import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandSeparator,
} from '@/components/ui/command';
import {
  LayoutDashboard,
  FolderKanban,
  Users,
  FileText,
  DollarSign,
  Bell,
  Settings,
  Plus,
  Search,
  Sun,
  Moon,
  LogOut,
  Sparkles,
  Calendar,
  Building2,
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useTheme } from 'next-themes';

interface SearchResult {
  id: string;
  name: string;
  type: 'project' | 'client';
}

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();

  // Global keyboard shortcut
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
    };

    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, []);

  // Search projects and clients
  const handleSearch = useCallback(async (query: string) => {
    setSearchQuery(query);
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    try {
      const [projectsRes, clientsRes] = await Promise.all([
        supabase
          .from('projects')
          .select('id, name')
          .ilike('name', `%${query}%`)
          .limit(5),
        supabase
          .from('clientes')
          .select('id, nome_completo')
          .ilike('nome_completo', `%${query}%`)
          .limit(5),
      ]);

      const results: SearchResult[] = [
        ...(projectsRes.data || []).map((p) => ({
          id: p.id,
          name: p.name,
          type: 'project' as const,
        })),
        ...(clientsRes.data || []).map((c) => ({
          id: c.id,
          name: c.nome_completo,
          type: 'client' as const,
        })),
      ];

      setSearchResults(results);
    } catch (error) {
      console.error('Search error:', error);
    }
  }, []);

  const runCommand = useCallback((command: () => void) => {
    setOpen(false);
    command();
  }, []);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput
        placeholder="Buscar ou executar comando..."
        value={searchQuery}
        onValueChange={handleSearch}
      />
      <CommandList>
        <CommandEmpty>Nenhum resultado encontrado.</CommandEmpty>

        {/* Search Results */}
        {searchResults.length > 0 && (
          <CommandGroup heading="Resultados da Busca">
            {searchResults.map((result) => (
              <CommandItem
                key={`${result.type}-${result.id}`}
                onSelect={() =>
                  runCommand(() =>
                    navigate(
                      result.type === 'project'
                        ? `/projetos/${result.id}`
                        : `/clientes`
                    )
                  )
                }
              >
                {result.type === 'project' ? (
                  <FolderKanban className="mr-2 h-4 w-4" />
                ) : (
                  <Building2 className="mr-2 h-4 w-4" />
                )}
                <span>{result.name}</span>
                <span className="ml-auto text-xs text-muted-foreground">
                  {result.type === 'project' ? 'Projeto' : 'Cliente'}
                </span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        <CommandSeparator />

        {/* Navigation */}
        <CommandGroup heading="Navegação">
          <CommandItem onSelect={() => runCommand(() => navigate('/dashboard'))}>
            <LayoutDashboard className="mr-2 h-4 w-4" />
            <span>Dashboard</span>
          </CommandItem>
          <CommandItem onSelect={() => runCommand(() => navigate('/projects'))}>
            <FolderKanban className="mr-2 h-4 w-4" />
            <span>Projetos</span>
          </CommandItem>
          <CommandItem onSelect={() => runCommand(() => navigate('/clientes'))}>
            <Users className="mr-2 h-4 w-4" />
            <span>Clientes</span>
          </CommandItem>
          <CommandItem onSelect={() => runCommand(() => navigate('/propostas'))}>
            <FileText className="mr-2 h-4 w-4" />
            <span>Propostas</span>
          </CommandItem>
          <CommandItem onSelect={() => runCommand(() => navigate('/financeiro'))}>
            <DollarSign className="mr-2 h-4 w-4" />
            <span>Financeiro</span>
          </CommandItem>
          <CommandItem onSelect={() => runCommand(() => navigate('/alertas'))}>
            <Bell className="mr-2 h-4 w-4" />
            <span>Alertas</span>
          </CommandItem>
          <CommandItem onSelect={() => runCommand(() => navigate('/equipe'))}>
            <Users className="mr-2 h-4 w-4" />
            <span>Equipe</span>
          </CommandItem>
          <CommandItem onSelect={() => runCommand(() => navigate('/agente-ia'))}>
            <Sparkles className="mr-2 h-4 w-4" />
            <span>Agente IA</span>
          </CommandItem>
        </CommandGroup>

        <CommandSeparator />

        {/* Quick Actions */}
        <CommandGroup heading="Ações Rápidas">
          <CommandItem
            onSelect={() =>
              runCommand(() => navigate('/projects', { state: { openNewProject: true } }))
            }
          >
            <Plus className="mr-2 h-4 w-4" />
            <span>Criar Novo Projeto</span>
          </CommandItem>
          <CommandItem
            onSelect={() => runCommand(() => navigate('/clientes/novo'))}
          >
            <Plus className="mr-2 h-4 w-4" />
            <span>Adicionar Cliente</span>
          </CommandItem>
          <CommandItem
            onSelect={() =>
              runCommand(() => navigate('/propostas', { state: { openNewProposta: true } }))
            }
          >
            <Plus className="mr-2 h-4 w-4" />
            <span>Nova Proposta</span>
          </CommandItem>
        </CommandGroup>

        <CommandSeparator />

        {/* System */}
        <CommandGroup heading="Sistema">
          <CommandItem
            onSelect={() =>
              runCommand(() => setTheme(theme === 'dark' ? 'light' : 'dark'))
            }
          >
            {theme === 'dark' ? (
              <Sun className="mr-2 h-4 w-4" />
            ) : (
              <Moon className="mr-2 h-4 w-4" />
            )}
            <span>Alternar Tema</span>
          </CommandItem>
          <CommandItem onSelect={() => runCommand(() => navigate('/configuracoes'))}>
            <Settings className="mr-2 h-4 w-4" />
            <span>Configurações</span>
          </CommandItem>
          <CommandItem onSelect={() => runCommand(handleLogout)}>
            <LogOut className="mr-2 h-4 w-4" />
            <span>Sair</span>
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
